import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, GraduationCap, BookOpen, Search, Filter, ChevronDown, ChevronUp,
  ArrowLeft, RefreshCw, Loader2, Edit2, Trash2, Plus, X, Save, UserPlus, Check,
  Upload, Database, AlertTriangle, FileText, Eye
} from 'lucide-react';
import { Logo } from '../components/Logo';
import {
  getAllStudents,
  getAllParents,
  getAllFaculty,
  addStudent,
  addParent,
  addFaculty,
  updateStudent,
  updateParent,
  updateFaculty,
  deleteStudent,
  deleteParent,
  deleteFaculty,
  bulkImportStudents,
  bulkImportParents,
  bulkImportFaculty,
  clearCollection
} from '../services/rosterService';

// Valid grades for validation
const VALID_GRADES = ['PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8', 'MS'];

// Grade values that indicate a student (not faculty role)
const STUDENT_GRADE_VALUES = ['PK', 'PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8', 'MS'];

// Faculty role indicators in Grade or Type columns
const FACULTY_ROLE_INDICATORS = [
  'PE', 'Music', 'Spanish', 'Art', 'Technology', 'Theatre', 'Religion',
  'MS Art', 'MS Math', 'MS Religion', 'MS Science', 'MS English', 'MS History'
];

// Modal component (defined outside to prevent re-creation on each render)
const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className={`bg-white rounded-xl shadow-2xl ${wide ? 'w-full max-w-4xl' : 'w-full max-w-lg'} mx-4 max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X size={24} />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// Toast component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600';

  return (
    <div className={`fixed bottom-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-xl flex items-center space-x-2 z-50 animate-pulse`}>
      {type === 'success' && <Check size={20} />}
      {type === 'error' && <X size={20} />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <X size={16} />
      </button>
    </div>
  );
};

// Universal Patron Parser
const parsePatronReport = (rawText) => {
  const lines = rawText.trim().split('\n');
  if (lines.length < 2) {
    return { students: [], parents: [], faculty: [], skipped: [], errors: [] };
  }

  // Parse header to find column indices
  const headerLine = lines[0];
  const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());

  const colIndex = {
    name: headers.findIndex(h => h === 'name' || h === 'last' || h === 'lastname'),
    first: headers.findIndex(h => h === 'first' || h === 'firstname'),
    comment: headers.findIndex(h => h === 'comment' || h === 'comments' || h === 'note'),
    sup: headers.findIndex(h => h === 'sup' || h === 'supervisor' || h === 'teacher'),
    type: headers.findIndex(h => h === 'type' || h === 'patrontype'),
    grade: headers.findIndex(h => h === 'grade' || h === 'gradelevel'),
    barcode: headers.findIndex(h => h === 'barcode' || h === 'id'),
  };

  const students = [];
  const parents = [];
  const faculty = [];
  const skipped = [];
  const errors = [];

  // Process each data row
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split('\t').map(c => c.trim());

    try {
      const lastName = colIndex.name >= 0 ? cols[colIndex.name] || '' : '';
      const firstName = colIndex.first >= 0 ? cols[colIndex.first] || '' : '';
      const comment = colIndex.comment >= 0 ? cols[colIndex.comment] || '' : '';
      const sup = colIndex.sup >= 0 ? cols[colIndex.sup] || '' : '';
      const type = colIndex.type >= 0 ? cols[colIndex.type] || '' : '';
      const grade = colIndex.grade >= 0 ? cols[colIndex.grade] || '' : '';
      const barcode = colIndex.barcode >= 0 ? cols[colIndex.barcode] || '' : '';

      // Skip invalid rows
      if (!lastName || lastName.toLowerCase().startsWith('book')) {
        skipped.push({ line: i + 1, reason: 'Invalid or book entry', data: line });
        continue;
      }

      // Format name: handle special cases like "Sr." prefix
      let fullName;
      if (lastName.toLowerCase().startsWith('sr.') || lastName.toLowerCase() === 'sr') {
        // "Sr." should stay at the beginning: "Sr. Maria Miguel"
        fullName = `${lastName} ${firstName}`.trim();
      } else {
        // Standard format: "LastName, FirstName"
        fullName = firstName ? `${lastName}, ${firstName}` : lastName;
      }

      const typeLower = type.toLowerCase();
      const gradeLower = grade.toLowerCase();
      const supLower = sup.toLowerCase();

      // Determine patron category
      // 1. PARENTS - Type = "Parent"
      if (typeLower === 'parent') {
        const children = parseChildrenFromComment(comment);
        parents.push({
          name: fullName,
          children: children,
          barcode: barcode,
          pin: ''
        });
        continue;
      }

      // 2. FACULTY/STAFF - Type is Faculty/Staff/Admin, or has role-like grade
      const isFacultyType = ['faculty', 'staff', 'admin', 'administrator'].includes(typeLower);
      const isFacultyRole = FACULTY_ROLE_INDICATORS.some(role =>
        gradeLower.includes(role.toLowerCase()) || supLower.includes(role.toLowerCase())
      );
      const hasNonStudentGrade = grade && !STUDENT_GRADE_VALUES.some(g =>
        gradeLower === g.toLowerCase() || gradeLower === g
      );

      if (isFacultyType || isFacultyRole || (hasNonStudentGrade && !typeLower)) {
        faculty.push({
          name: fullName,
          role: type || grade || sup,
          grade: STUDENT_GRADE_VALUES.includes(grade.toUpperCase()) ? grade : '',
          barcode: barcode,
          pin: ''
        });
        continue;
      }

      // 3. STUDENTS - Has valid grade and not Parent/Faculty/Staff type
      const normalizedGrade = normalizeGrade(grade);
      if (normalizedGrade && !['parent', 'faculty', 'staff', 'admin'].includes(typeLower)) {
        students.push({
          name: fullName,
          teacher: sup,
          grade: normalizedGrade,
          barcode: barcode,
          pin: ''
        });
        continue;
      }

      // Couldn't categorize
      skipped.push({ line: i + 1, reason: 'Could not determine patron type', data: { fullName, type, grade, sup } });

    } catch (err) {
      errors.push({ line: i + 1, error: err.message, data: line });
    }
  }

  return { students, parents, faculty, skipped, errors };
};

// Parse children names from Comment field
const parseChildrenFromComment = (comment) => {
  if (!comment) return [];

  // Match patterns like "Parent of Child Name" or "Parent of Child1 and Child2"
  const parentOfMatch = comment.match(/parent of\s+(.+)/i);
  if (!parentOfMatch) return [];

  const childrenStr = parentOfMatch[1];

  // Split on " and " or ", " or ","
  const children = childrenStr
    .split(/(?:\s+and\s+|,\s*)/i)
    .map(c => c.trim())
    .filter(c => c.length > 0);

  return children;
};

// Normalize grade values
const normalizeGrade = (grade) => {
  if (!grade) return '';

  const g = grade.trim().toUpperCase();

  // Direct matches
  if (VALID_GRADES.includes(g)) return g;

  // PK variations
  if (g === 'PK' || g === 'PRE-K' || g === 'PREK') return 'PK4';
  if (g === 'PK-3' || g === 'PRE-K3') return 'PK3';
  if (g === 'PK-4' || g === 'PRE-K4') return 'PK4';

  // Kindergarten variations
  if (g === 'KINDER' || g === 'KINDERGARTEN' || g === '0') return 'K';

  // Middle school variations
  if (['6', '7', '8', 'MIDDLE', 'MIDDLE SCHOOL'].includes(g)) {
    if (g === 'MIDDLE' || g === 'MIDDLE SCHOOL') return 'MS';
    return g;
  }

  // Number grades
  if (/^[1-8]$/.test(g)) return g;

  return '';
};

function UrdsWell() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState('students');

  // Data state - from Firebase
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [teacherFilter, setTeacherFilter] = useState('all');

  // Sort state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Modal/Edit state
  const [editingRecord, setEditingRecord] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  // Data Management state
  const [showMigration, setShowMigration] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);

  // Parser state
  const [rawPatronData, setRawPatronData] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    teacher: '',
    grade: '',
    barcode: '',
    pin: '',
    role: '',
    children: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [newChild, setNewChild] = useState('');

  // Fetch data from Firebase
  const fetchData = useCallback(async () => {
    if (!currentUser?.uid) {
      setError('No user logged in');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [studentsData, parentsData, facultyData] = await Promise.all([
        getAllStudents(currentUser.uid),
        getAllParents(currentUser.uid),
        getAllFaculty(currentUser.uid)
      ]);
      setStudents(studentsData);
      setParents(parentsData);
      setFaculty(facultyData);
    } catch (err) {
      console.error('Error fetching roster data:', err);
      setError('Failed to load roster data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Fetch on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique grades and teachers from Firebase data
  const grades = useMemo(() => {
    const uniqueGrades = [...new Set(students.map(s => s.grade).filter(Boolean))];
    return uniqueGrades.sort((a, b) => {
      const order = ['PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8', 'MS'];
      return order.indexOf(a) - order.indexOf(b);
    });
  }, [students]);

  const teachers = useMemo(() => {
    return [...new Set(students.map(s => s.teacher).filter(Boolean))].sort();
  }, [students]);

  // Filter and sort students
  const filteredStudents = useMemo(() => {
    let result = [...students];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s =>
        (s.name && s.name.toLowerCase().includes(query)) ||
        (s.teacher && s.teacher.toLowerCase().includes(query)) ||
        (s.grade && s.grade.toLowerCase().includes(query)) ||
        (s.barcode && s.barcode.includes(query))
      );
    }

    if (gradeFilter !== 'all') {
      result = result.filter(s => s.grade === gradeFilter);
    }

    if (teacherFilter !== 'all') {
      result = result.filter(s => s.teacher === teacherFilter);
    }

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'name') {
        aVal = a.name || '';
        bVal = b.name || '';
      } else if (sortField === 'teacher') {
        aVal = a.teacher || '';
        bVal = b.teacher || '';
      } else if (sortField === 'grade') {
        const order = ['PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8', 'MS'];
        aVal = order.indexOf(a.grade);
        bVal = order.indexOf(b.grade);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [students, searchQuery, gradeFilter, teacherFilter, sortField, sortDirection]);

  // Filter and sort parents
  const filteredParents = useMemo(() => {
    let result = [...parents];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p =>
        (p.name && p.name.toLowerCase().includes(query)) ||
        (p.barcode && p.barcode.includes(query)) ||
        (p.children && p.children.some(c => c.toLowerCase().includes(query)))
      );
    }

    result.sort((a, b) => {
      const aVal = a.name || '';
      const bVal = b.name || '';
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [parents, searchQuery, sortDirection]);

  // Filter and sort faculty
  const filteredFaculty = useMemo(() => {
    let result = [...faculty];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(f =>
        (f.name && f.name.toLowerCase().includes(query)) ||
        (f.role && f.role.toLowerCase().includes(query)) ||
        (f.barcode && f.barcode.includes(query))
      );
    }

    result.sort((a, b) => {
      const aVal = a.name || '';
      const bVal = b.name || '';
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [faculty, searchQuery, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <ChevronUp size={16} className="inline ml-1" /> :
      <ChevronDown size={16} className="inline ml-1" />;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Failed to log out', err);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.barcode && !/^\d*$/.test(formData.barcode)) {
      errors.barcode = 'Barcode must be numeric';
    }

    if (activeTab === 'students' && formData.grade && !VALID_GRADES.includes(formData.grade)) {
      errors.grade = 'Invalid grade. Use: ' + VALID_GRADES.join(', ');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Open add modal
  const handleAdd = () => {
    setFormData({
      name: '',
      teacher: '',
      grade: '',
      barcode: '',
      pin: '',
      role: '',
      children: []
    });
    setFormErrors({});
    setNewChild('');
    setShowAddModal(true);
  };

  // Open edit modal
  const handleEdit = (record) => {
    setFormData({
      name: record.name || '',
      teacher: record.teacher || '',
      grade: record.grade || '',
      barcode: record.barcode || '',
      pin: record.pin || '',
      role: record.role || '',
      children: record.children || []
    });
    setFormErrors({});
    setNewChild('');
    setEditingRecord(record);
  };

  // Save (add or update)
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingRecord) {
        // Update existing
        if (activeTab === 'students') {
          await updateStudent(currentUser.uid, editingRecord.id, formData);
        } else if (activeTab === 'parents') {
          await updateParent(currentUser.uid, editingRecord.id, formData);
        } else {
          await updateFaculty(currentUser.uid, editingRecord.id, formData);
        }
        showToast('Record updated successfully');
      } else {
        // Add new
        if (activeTab === 'students') {
          await addStudent(currentUser.uid, formData);
        } else if (activeTab === 'parents') {
          await addParent(currentUser.uid, formData);
        } else {
          await addFaculty(currentUser.uid, formData);
        }
        showToast('Record added successfully');
      }

      setEditingRecord(null);
      setShowAddModal(false);
      await fetchData();
    } catch (err) {
      console.error('Save error:', err);
      showToast('Failed to save: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    setSaving(true);
    try {
      if (activeTab === 'students') {
        await deleteStudent(currentUser.uid, deleteConfirm.id);
      } else if (activeTab === 'parents') {
        await deleteParent(currentUser.uid, deleteConfirm.id);
      } else {
        await deleteFaculty(currentUser.uid, deleteConfirm.id);
      }
      showToast('Record deleted successfully');
      setDeleteConfirm(null);
      await fetchData();
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Failed to delete: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Parse patron data
  const handleParseData = () => {
    if (!rawPatronData.trim()) {
      showToast('Please paste patron data first', 'error');
      return;
    }

    const result = parsePatronReport(rawPatronData);
    setParsedData(result);
    setShowPreview(true);
  };

  // Import parsed data to Firebase
  const handleImportParsedData = async () => {
    if (!currentUser?.uid || !parsedData) {
      showToast('No data to import', 'error');
      return;
    }

    setMigrating(true);
    setMigrationResults(null);

    try {
      const [studentsResult, parentsResult, facultyResult] = await Promise.all([
        parsedData.students.length > 0 ? bulkImportStudents(currentUser.uid, parsedData.students) : { success: 0, failed: 0 },
        parsedData.parents.length > 0 ? bulkImportParents(currentUser.uid, parsedData.parents) : { success: 0, failed: 0 },
        parsedData.faculty.length > 0 ? bulkImportFaculty(currentUser.uid, parsedData.faculty) : { success: 0, failed: 0 }
      ]);

      setMigrationResults({
        students: studentsResult,
        parents: parentsResult,
        faculty: facultyResult
      });

      showToast('Import complete!', 'success');
      setRawPatronData('');
      setParsedData(null);
      setShowPreview(false);
      await fetchData();
    } catch (err) {
      console.error('Import error:', err);
      showToast('Import failed: ' + err.message, 'error');
    } finally {
      setMigrating(false);
    }
  };

  // Clear all data for current user
  const handleClearAll = async () => {
    if (!currentUser?.uid) return;

    if (!window.confirm('Are you sure you want to delete ALL roster data? This cannot be undone!')) {
      return;
    }

    setMigrating(true);
    try {
      await Promise.all([
        clearCollection(currentUser.uid, 'students'),
        clearCollection(currentUser.uid, 'parents'),
        clearCollection(currentUser.uid, 'faculty')
      ]);

      showToast('All data cleared', 'success');
      await fetchData();
    } catch (err) {
      console.error('Clear error:', err);
      showToast('Failed to clear data: ' + err.message, 'error');
    } finally {
      setMigrating(false);
    }
  };

  // Children management for parents
  const addChild = () => {
    if (newChild.trim()) {
      setFormData(prev => ({
        ...prev,
        children: [...prev.children, newChild.trim()]
      }));
      setNewChild('');
    }
  };

  const removeChild = (index) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const getTabCount = (tab) => {
    switch (tab) {
      case 'students': return students.length;
      case 'parents': return parents.length;
      case 'faculty': return faculty.length;
      default: return 0;
    }
  };

  const getFilteredCount = () => {
    switch (activeTab) {
      case 'students': return filteredStudents.length;
      case 'parents': return filteredParents.length;
      case 'faculty': return filteredFaculty.length;
      default: return 0;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">Urd's Well</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="hidden md:block text-right">
                <p className="text-sm text-purple-300">Total Patrons</p>
                <p className="text-xl font-bold">
                  {students.length + parents.length + faculty.length}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Subheader */}
      <div className="bg-slate-200 border-b border-slate-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Urd's Well</h2>
              <p className="text-slate-600 mt-1">The Record of All Fates</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowMigration(!showMigration)}
                className="flex items-center text-slate-600 hover:text-slate-800 transition-colors"
              >
                <Database size={18} className="mr-2" />
                Data Management
              </button>
              <button
                onClick={fetchData}
                disabled={loading}
                className="flex items-center text-slate-600 hover:text-slate-800 transition-colors"
              >
                <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => navigate('/asgard')}
                className="flex items-center text-slate-600 hover:text-slate-800 transition-colors"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to Asgard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-700">
            <X size={20} className="mr-2" />
            {error}
            <button onClick={fetchData} className="ml-auto text-red-600 hover:text-red-800 underline">
              Try Again
            </button>
          </div>
        )}

        {/* Data Management Panel */}
        {showMigration && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center">
              <Database size={20} className="mr-2" />
              Data Management
            </h3>

            {/* Import Section */}
            <div className="bg-white rounded-lg p-4 border border-amber-100 mb-4">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <Upload size={18} className="mr-2 text-green-600" />
                Import Patron Data
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                Paste tab-separated patron data from your library system (Follett, Alexandria, etc.).
                Expected columns: Name, First, Comment, Sup (teacher), Type, Grade
              </p>

              <textarea
                value={rawPatronData}
                onChange={(e) => setRawPatronData(e.target.value)}
                placeholder="Paste your patron report here...&#10;&#10;Expected format (tab-separated):&#10;Name&#9;First&#9;Comment&#9;Sup&#9;Type&#9;Grade&#9;Out&#9;Hold&#9;Overdue&#10;Smith&#9;John&#9;&#9;Kelley&#9;&#9;4&#9;0&#9;0&#9;0&#10;Jones&#9;Mary&#9;Parent of John Smith&#9;&#9;Parent&#9;&#9;0&#9;0&#9;0"
                className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
              />

              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleParseData}
                  disabled={!rawPatronData.trim()}
                  className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Eye size={18} className="mr-2" />
                  Parse & Preview
                </button>
                {parsedData && (
                  <button
                    onClick={handleImportParsedData}
                    disabled={migrating}
                    className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {migrating ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload size={18} className="mr-2" />
                        Import {parsedData.students.length + parsedData.parents.length + parsedData.faculty.length} Records
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Preview Results */}
              {showPreview && parsedData && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-semibold text-gray-800 mb-2">Parse Results:</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                    <div className="bg-green-100 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-green-700">{parsedData.students.length}</p>
                      <p className="text-sm text-green-600">Students</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-blue-700">{parsedData.parents.length}</p>
                      <p className="text-sm text-blue-600">Parents</p>
                    </div>
                    <div className="bg-yellow-100 p-3 rounded text-center">
                      <p className="text-2xl font-bold text-yellow-700">{parsedData.faculty.length}</p>
                      <p className="text-sm text-yellow-600">Faculty</p>
                    </div>
                  </div>

                  {parsedData.skipped.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-orange-600 font-medium">
                        {parsedData.skipped.length} rows skipped:
                      </p>
                      <div className="max-h-32 overflow-y-auto mt-1 text-xs text-gray-600 bg-orange-50 p-2 rounded">
                        {parsedData.skipped.slice(0, 10).map((s, i) => (
                          <div key={i}>Line {s.line}: {s.reason}</div>
                        ))}
                        {parsedData.skipped.length > 10 && (
                          <div className="text-orange-500">...and {parsedData.skipped.length - 10} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {parsedData.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-red-600 font-medium">
                        {parsedData.errors.length} errors:
                      </p>
                      <div className="max-h-32 overflow-y-auto mt-1 text-xs text-red-600 bg-red-50 p-2 rounded">
                        {parsedData.errors.map((e, i) => (
                          <div key={i}>Line {e.line}: {e.error}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sample Preview */}
                  {parsedData.students.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-700 font-medium mb-1">Sample Students:</p>
                      <div className="text-xs text-gray-600 bg-white p-2 rounded border max-h-24 overflow-y-auto">
                        {parsedData.students.slice(0, 5).map((s, i) => (
                          <div key={i}>{s.name} - {s.teacher} - Grade {s.grade}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {parsedData.parents.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-700 font-medium mb-1">Sample Parents:</p>
                      <div className="text-xs text-gray-600 bg-white p-2 rounded border max-h-24 overflow-y-auto">
                        {parsedData.parents.slice(0, 5).map((p, i) => (
                          <div key={i}>{p.name} - Children: {p.children.join(', ') || 'None listed'}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Migration Results */}
              {migrationResults && (
                <div className="mt-4 text-sm">
                  <p className="text-green-700">
                    Students: {migrationResults.students.success} imported
                    {migrationResults.students.failed > 0 && `, ${migrationResults.students.failed} failed`}
                  </p>
                  <p className="text-green-700">
                    Parents: {migrationResults.parents.success} imported
                    {migrationResults.parents.failed > 0 && `, ${migrationResults.parents.failed} failed`}
                  </p>
                  <p className="text-green-700">
                    Faculty: {migrationResults.faculty.success} imported
                    {migrationResults.faculty.failed > 0 && `, ${migrationResults.faculty.failed} failed`}
                  </p>
                </div>
              )}
            </div>

            {/* Clear Section */}
            <div className="bg-white rounded-lg p-4 border border-amber-100">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <AlertTriangle size={18} className="mr-2 text-red-600" />
                Clear All Data
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Remove all roster data for your account. This action cannot be undone!
              </p>
              <button
                onClick={handleClearAll}
                disabled={migrating}
                className="flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {migrating ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} className="mr-2" />
                    Clear All Data
                  </>
                )}
              </button>
            </div>

            <p className="mt-4 text-xs text-amber-700">
              User ID: {currentUser?.uid || 'Not logged in'}
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && !error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={48} className="text-purple-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading roster data...</p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex space-x-1 bg-white rounded-lg shadow-md p-1 mb-6">
              <button
                onClick={() => { setActiveTab('students'); setGradeFilter('all'); setTeacherFilter('all'); setSearchQuery(''); }}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'students'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookOpen size={20} />
                <span>Students</span>
                <span className={`px-2 py-0.5 rounded-full text-sm ${
                  activeTab === 'students' ? 'bg-purple-500' : 'bg-gray-200'
                }`}>
                  {getTabCount('students')}
                </span>
              </button>
              <button
                onClick={() => { setActiveTab('parents'); setGradeFilter('all'); setTeacherFilter('all'); setSearchQuery(''); }}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'parents'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Users size={20} />
                <span>Parents</span>
                <span className={`px-2 py-0.5 rounded-full text-sm ${
                  activeTab === 'parents' ? 'bg-purple-500' : 'bg-gray-200'
                }`}>
                  {getTabCount('parents')}
                </span>
              </button>
              <button
                onClick={() => { setActiveTab('faculty'); setGradeFilter('all'); setTeacherFilter('all'); setSearchQuery(''); }}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'faculty'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <GraduationCap size={20} />
                <span>Faculty</span>
                <span className={`px-2 py-0.5 rounded-full text-sm ${
                  activeTab === 'faculty' ? 'bg-purple-500' : 'bg-gray-200'
                }`}>
                  {getTabCount('faculty')}
                </span>
              </button>
            </div>

            {/* Search, Filters, and Add Button */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Filters for Students tab */}
                {activeTab === 'students' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <Filter size={20} className="text-gray-400" />
                      <select
                        value={gradeFilter}
                        onChange={(e) => setGradeFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="all">All Grades</option>
                        {grades.map(grade => (
                          <option key={grade} value={grade}>{grade}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        value={teacherFilter}
                        onChange={(e) => setTeacherFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="all">All Teachers</option>
                        {teachers.map(teacher => (
                          <option key={teacher} value={teacher}>{teacher}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Add Button */}
                <button
                  onClick={handleAdd}
                  className="flex items-center bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <UserPlus size={20} className="mr-2" />
                  Add {activeTab === 'students' ? 'Student' : activeTab === 'parents' ? 'Parent' : 'Faculty'}
                </button>
              </div>

              {/* Results count */}
              <div className="mt-3 text-sm text-gray-500">
                Showing {getFilteredCount()} of {getTabCount(activeTab)} {activeTab}
              </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {activeTab === 'students' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        Student Name <SortIcon field="name" />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('teacher')}
                      >
                        Teacher <SortIcon field="teacher" />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('grade')}
                      >
                        Grade <SortIcon field="grade" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Barcode
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <BookOpen size={16} className="text-green-500 mr-2" />
                            <span className="font-medium text-gray-900">{student.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {student.teacher || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                            {student.grade || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono text-sm">
                          {student.barcode || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleEdit(student)}
                            className="text-blue-600 hover:text-blue-800 p-1 mr-2"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(student)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          No students found matching your search criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'parents' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        Parent Name <SortIcon field="name" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Children
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Barcode
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredParents.map((parent) => (
                      <tr key={parent.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users size={16} className="text-blue-500 mr-2" />
                            <span className="font-medium text-gray-900">{parent.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {parent.children && parent.children.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {parent.children.map((child, idx) => (
                                <span key={idx} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  {child}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono text-sm">
                          {parent.barcode || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleEdit(parent)}
                            className="text-blue-600 hover:text-blue-800 p-1 mr-2"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(parent)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredParents.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          No parents found matching your search criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'faculty' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        Faculty Name <SortIcon field="name" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Barcode
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredFaculty.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <GraduationCap size={16} className="text-yellow-600 mr-2" />
                            <span className="font-medium text-gray-900">{member.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {member.role || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono text-sm">
                          {member.barcode || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleEdit(member)}
                            className="text-blue-600 hover:text-blue-800 p-1 mr-2"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(member)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredFaculty.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          No faculty found matching your search criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingRecord) && (
        <Modal
          title={editingRecord ? `Edit ${activeTab.slice(0, -1)}` : `Add New ${activeTab.slice(0, -1)}`}
          onClose={() => { setShowAddModal(false); setEditingRecord(null); }}
        >
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                  formErrors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Last, First"
              />
              {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
            </div>

            {/* Student-specific fields */}
            {activeTab === 'students' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                  <input
                    type="text"
                    value={formData.teacher}
                    onChange={(e) => setFormData(prev => ({ ...prev, teacher: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Teacher name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      formErrors.grade ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Grade</option>
                    {VALID_GRADES.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {formErrors.grade && <p className="text-red-500 text-sm mt-1">{formErrors.grade}</p>}
                </div>
              </>
            )}

            {/* Parent-specific fields */}
            {activeTab === 'parents' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Children</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.children.map((child, idx) => (
                    <span key={idx} className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">
                      {child}
                      <button onClick={() => removeChild(idx)} className="ml-1 hover:text-blue-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newChild}
                    onChange={(e) => setNewChild(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChild())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Child name"
                  />
                  <button
                    onClick={addChild}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>
            )}

            {/* Faculty-specific fields */}
            {activeTab === 'faculty' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Teacher, Administrator, etc."
                />
              </div>
            )}

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                    formErrors.barcode ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Numeric barcode"
                />
                {formErrors.barcode && <p className="text-red-500 text-sm mt-1">{formErrors.barcode}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PIN</label>
                <input
                  type="text"
                  value={formData.pin}
                  onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="PIN code"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => { setShowAddModal(false); setEditingRecord(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal
          title="Confirm Delete"
          onClose={() => setDeleteConfirm(null)}
        >
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 size={24} className="text-red-600" />
            </div>
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
            </p>
            <p className="text-gray-500 text-sm mb-6">
              This action cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} className="mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default UrdsWell;
